import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema } from "./_envelope";

/**
 * Outcome of a headless Blueberry intake attempt. `success` plus the driver's
 * failure classes (kept in sync with `HeadlessIntakeFailureClass` in
 * `@repo/blueberry`), plus `fingerprint_drift` which the canary emits when a run
 * is otherwise green but the intake surface changed shape. String `value` so it
 * can be counted by category (TAXONOMY.md "value field" rule).
 *
 * NB: this list is intentionally re-declared here rather than imported from
 * `@repo/blueberry` — the statsig package is brand-blind capability
 * infrastructure and must not depend on a feature package.
 */
export const HEADLESS_INTAKE_OUTCOMES = {
  SUCCESS: "success",
  CSRF_META_MISSING: "csrf_meta_missing",
  CSRF_403: "csrf_403",
  BOOTSTRAP_FAILED: "bootstrap_failed",
  CHILD_ID_UNRESOLVED: "child_id_unresolved",
  REQUIRED_QUESTIONS_REJECTED: "required_questions_rejected",
  MH_DID_NOT_ADVANCE: "mh_did_not_advance",
  MH_LOOP_STUCK: "mh_loop_stuck",
  STEP_DID_NOT_ADVANCE: "step_did_not_advance",
  UNEXPECTED_STEP: "unexpected_step",
  LOCATION_REJECTED: "location_rejected",
  CONFIRMATION_FORM_MISSING: "confirmation_form_missing",
  VISIT_NEVER_SUBMITTED: "visit_never_submitted",
  UPSTREAM_ERROR: "upstream_error",
  TIMEOUT: "timeout",
  FINGERPRINT_DRIFT: "fingerprint_drift",
} as const;

export type HeadlessIntakeOutcome =
  (typeof HEADLESS_INTAKE_OUTCOMES)[keyof typeof HEADLESS_INTAKE_OUTCOMES];

/**
 * One headless Blueberry intake attempt — canary or (future) production. Emitted
 * by the `blueberry-intake-canary` cron on every run and, once wired, by the
 * production headless call site. `value` is the outcome (`success` or a failure
 * class), so counting `value != success` grouped by `is_canary` separates
 * "the integration broke" (canary) from "a patient was affected" (production).
 *
 * See the PE-1468 approach for the failure taxonomy and the drift-detection
 * design, and `HEADLESS_INTAKE.md` for the intake flow this instruments.
 */
export const headlessIntakeAttempted = defineEvent({
  eventName: "headless_intake_attempted",
  eventVersion: 1,
  // Outcome string — success or the failure class.
  valueSchema: z.enum([
    HEADLESS_INTAKE_OUTCOMES.SUCCESS,
    HEADLESS_INTAKE_OUTCOMES.CSRF_META_MISSING,
    HEADLESS_INTAKE_OUTCOMES.CSRF_403,
    HEADLESS_INTAKE_OUTCOMES.BOOTSTRAP_FAILED,
    HEADLESS_INTAKE_OUTCOMES.CHILD_ID_UNRESOLVED,
    HEADLESS_INTAKE_OUTCOMES.REQUIRED_QUESTIONS_REJECTED,
    HEADLESS_INTAKE_OUTCOMES.MH_DID_NOT_ADVANCE,
    HEADLESS_INTAKE_OUTCOMES.MH_LOOP_STUCK,
    HEADLESS_INTAKE_OUTCOMES.STEP_DID_NOT_ADVANCE,
    HEADLESS_INTAKE_OUTCOMES.UNEXPECTED_STEP,
    HEADLESS_INTAKE_OUTCOMES.LOCATION_REJECTED,
    HEADLESS_INTAKE_OUTCOMES.CONFIRMATION_FORM_MISSING,
    HEADLESS_INTAKE_OUTCOMES.VISIT_NEVER_SUBMITTED,
    HEADLESS_INTAKE_OUTCOMES.UPSTREAM_ERROR,
    HEADLESS_INTAKE_OUTCOMES.TIMEOUT,
    HEADLESS_INTAKE_OUTCOMES.FINGERPRINT_DRIFT,
  ]),
  metadataSchema: baseEnvelopeSchema
    .extend({
      // Always "blueberry" — kept explicit so this reads uniformly beside the
      // MDI events and dashboards can filter by provider.
      provider: z.literal("blueberry").optional(),
      // Whether this was a synthetic canary run (true) or a real patient attempt
      // (false) — the slicer that separates integration health from patient impact.
      is_canary: z.boolean().optional(),
      // The deployment target the run executed on, from `VERCEL_TARGET_ENV`
      // ("production" | "staging" | "preview" | "development"). Before Sept 2026
      // this was derived from the tier rather than the deployment; the values
      // agree historically, because only one tier ran in one environment then.
      environment: z.string().optional(),
      // Canary tier: "prod_draft" (draft-only, safe) | "staging_submit" (full submit).
      tier: z.string().optional(),
      // Intake step the outcome was decided at (bootstrap, symptom_select, ...).
      step: z.string().optional(),
      // HTTP status observed at a failure, when there was one.
      http_status: z.number().int().optional(),
      // Whether the visit was actually submitted (confirmation/submit posted).
      submitted: z.boolean().optional(),
      // How many stale submitted visits this run retired before driving a new
      // one. Non-zero means an earlier run leaked a case into a clinician queue
      // and this run cleaned up after it — so the leak rate stays queryable
      // rather than being silently absorbed.
      swept: z.number().int().nonnegative().optional(),
      // Stale visits this run found but could NOT retire. Non-zero means cases
      // are sitting in a clinician's queue and the self-healing sweep isn't
      // working — the condition `swept` alone would hide, since a failed sweep
      // and a clean run both report `swept: 0`.
      sweep_stuck: z.number().int().nonnegative().optional(),
      // Submit tier only: whether the synthetic visit was cancelled afterwards,
      // so a clinician never works a canary case. Absent on the draft tier,
      // where nothing was submitted in the first place.
      visit_cancelled: z.boolean().optional(),
      // Wall-clock duration of the attempt.
      duration_ms: z.number().int().nonnegative().optional(),
      // Medical-history pages the questionnaire ran (0 for owlet_alarm).
      n_mh_pages: z.number().int().nonnegative().optional(),
      // The visit id from Blueberry's `/px/visit/<id>` intake URLs, when one was
      // allocated. Since their 2026-08 migration that is the visit's
      // `external_id` UUID, so it joins against the visits API directly.
      visit_id: z.string().optional(),
      // Structural fingerprint hash of the intake surface (drift detection).
      fingerprint: z.string().optional(),
      // Baseline fingerprint this run was compared against, on a drift outcome.
      expected_fingerprint: z.string().optional(),
    })
    .passthrough(),
});
